/**
 * The client half of `protocol.ts`: one WebSocket, and strict rules about when
 * it is allowed to exist.
 *
 * The single property this module must have is **silence**. The game is a
 * single-player game that other people can walk into. If there is no server, or
 * the URL is wrong, or the browser is offline, or a school network blocks
 * WebSockets, or a Worker is mid-deploy, nothing happens: no error, no dialog,
 * no console noise, no missing frame. The player keeps walking through the
 * yellow rooms and never learns that multiplayer was attempted. Every failure
 * path in here ends in "stay quiet and try again later", and `dispose` is the
 * only thing that ever ends it for good.
 *
 * Consequences of that, which shape the API:
 *
 *  - **Nothing here throws.** Sends, parses and handler callbacks are all
 *    wrapped. A subscriber that throws must not be able to take the socket
 *    down, let alone the frame loop.
 *  - **The game polls; the client does not push into the game.** `peers` is a
 *    live array the renderer reads once a frame. Only the things that genuinely
 *    are events — somebody hit you, somebody took the last almond water —
 *    arrive as callbacks.
 *  - **Everything the server says is advisory.** Offline, `take` is dropped on
 *    the floor and the game must pocket the item itself. Online, the game
 *    should wait for the `taken` echo, or two people both get the last drink.
 *    `online` is how you tell which world you are in.
 *
 * Threading a socket through a 60 Hz loop also means being careful about
 * traffic: `publish` throttles to `STATE_HZ` and collapses to a 1 Hz keepalive
 * when the player is standing still, so an idle tab costs a message a second
 * rather than fifteen.
 */

import {
  PROTOCOL_VERSION,
  STATE_HZ,
  type ClientMessage,
  type PeerId,
  type PeerState,
  type ServerMessage,
  type TakenItem,
} from './protocol';

/**
 * Path the socket is opened on when nothing else says otherwise. The Worker has
 * to answer a WebSocket upgrade here, on the same origin as the page; set
 * `VITE_NET_URL` at build time to point somewhere else (a dev Worker on
 * :8787, say).
 */
const DEFAULT_PATH = '/net';

/** Milliseconds between position updates. `STATE_HZ` is the contract. */
const SEND_INTERVAL_MS = 1000 / STATE_HZ;

/**
 * How long a *stationary* player may go without sending anything. Well inside
 * `PEER_TIMEOUT` so an AFK player is never dropped, and it turns standing still
 * from fifteen identical messages a second into one.
 */
const KEEPALIVE_MS = 1000;

/* Reconnection. The shape of these numbers is "never hammer a server that is
 * down": a first retry fast enough that a Worker restart is invisible, then
 * doubling to a half-minute ceiling. */
const RETRY_BASE_MS = 1200;
const RETRY_MAX_MS = 30_000;
/**
 * A connection that has *never* succeeded is a different situation: most likely
 * there is no server at all, and this build is simply single-player. So it
 * gives up entirely after a few tries rather than knocking on an empty door for
 * the rest of the session. `connect()` starts it over — the game calls that at
 * the top of each run, which is the natural moment to check again.
 */
const COLD_ATTEMPTS = 5;

/** Longest line `say` will put on the wire. Chat is a fallback, not a feature. */
const SAY_MAX = 140;

/** Movement below this is not worth a packet. Metres, and radians. */
const MOVE_EPSILON = 0.02;
const TURN_EPSILON = 0.01;
/** Points of health worth telling the world about. See `publish`. */
const HEALTH_EPSILON = 1;

/** Where the socket is in its life. Only `online` means messages will arrive. */
export type NetStatus =
  /** Never asked to connect, or deliberately disconnected. */
  | 'idle'
  /** A socket is open or opening but the server has not welcomed us yet. */
  | 'connecting'
  /** Welcomed. `self` and `worldSeed` are set. */
  | 'online'
  /**
   * Not connected and not going to be for a while — either backing off, or
   * given up (no server here, or the server refused us). Indistinguishable from
   * single-player by design.
   */
  | 'offline';

/**
 * Another player, as this client knows them.
 *
 * The record is mutated in place at `STATE_HZ` rather than replaced, so a view
 * may hold onto one across frames and the update path allocates nothing.
 */
export interface RemotePeer extends PeerState {
  /**
   * Their display name, or `''` when we have never been told it — which is the
   * case for everyone who was already on the floor when we arrived. See the
   * note on `handleWelcome`.
   */
  name: string;
  /** `performance.now()` when their last state arrived. Staleness, for views. */
  at: number;
}

/**
 * Things that happen *to* the player, as opposed to state they can read. Every
 * one of these is rare; positions deliberately do not appear here.
 */
export interface NetHandlers {
  /** The socket's life changed. Fired on every transition, including failures. */
  status(status: NetStatus): void;
  /**
   * Accepted into the world. `worldSeed` is the whole point of the protocol:
   * generate from it and you are standing in everyone else's building.
   */
  welcome(info: { you: PeerId; worldSeed: number; taken: readonly TakenItem[] }): void;
  /** Somebody joined the world (any floor). Mostly useful for learning names. */
  joined(peer: { id: PeerId; name: string; floor: number }): void;
  /** Somebody left, or we lost the connection and dropped everyone. */
  left(id: PeerId): void;
  /**
   * A supply is gone. Authoritative — remove it whatever the client thought.
   * `mine` is true when we were the one who claimed it, which is the signal to
   * actually pocket the item.
   */
  taken(e: { floor: number; item: number; by: PeerId; mine: boolean }): void;
  /** You were hit. Already range-checked by the server. */
  struck(e: { by: PeerId; amount: number }): void;
  said(e: { from: PeerId; text: string }): void;
  /** WebRTC signalling for voice. Opaque here; handed straight over. */
  signal(e: { from: PeerId; data: unknown }): void;
}

export interface NetClientOptions {
  /** Overrides the URL entirely. Empty or malformed means "no multiplayer". */
  url?: string;
  /** Name shown over this player's head. Trimmed and clamped. */
  name?: string;
}

/** The transport, as the game sees it. */
export interface NetClient {
  readonly status: NetStatus;
  /** True only when the server has welcomed us and the socket is open. */
  readonly online: boolean;
  /** Our own id, or null when offline. */
  readonly self: PeerId | null;
  /** The shared world's seed, or null when we have never been welcomed. */
  readonly worldSeed: number | null;
  /**
   * Everyone else on our floor, refreshed at `STATE_HZ`. Stable array identity;
   * empty when offline. Poll it, do not hold the elements past a frame.
   */
  readonly peers: readonly RemotePeer[];

  /**
   * Open a socket, if there is any prospect of one. Cheap and idempotent —
   * calling it while connected, connecting or backing off does nothing, and
   * calling it after the client gave up starts the attempts over.
   */
  connect(name?: string): void;
  /** Close and stay closed until `connect` is called again. */
  disconnect(): void;

  /**
   * Publish our position. Call every frame while alive and unpaused; the
   * throttling to `STATE_HZ` lives here so the caller never has to own a timer.
   * Sending nothing for a while is exactly right for a paused or dead player:
   * they stop moving for everyone else too.
   *
   * A change of `s.floor` is noticed and announced automatically.
   */
  publish(s: Omit<PeerState, 'id'>): void;

  /** Announce a floor change. `publish` does this for you; this is the manual door. */
  setFloor(floor: number): void;
  /** Claim a supply. Wait for the `taken` callback before pocketing it. */
  take(floor: number, item: number): void;
  /** Hit somebody. The server validates the range; see `STRIKE_RANGE`. */
  strike(target: PeerId, amount: number): void;
  say(text: string): void;
  signal(to: PeerId, data: unknown): void;

  /** True once anybody has claimed this supply. Also seeded from `welcome`. */
  isTaken(floor: number, item: number): boolean;
  /** Their name, or `''` if we were never told it. */
  nameOf(id: PeerId): string;

  /** Subscribe. Returns an unsubscribe function. Handlers may throw; nothing happens. */
  on(handlers: Partial<NetHandlers>): () => void;

  dispose(): void;
}

/**
 * Work out where to connect, or decide not to.
 *
 * Returning null is a normal outcome, not an error: opened from a `file://`
 * URL, or in a test harness with no `location`, there is no server to find and
 * the game is single-player. That is the same non-event as the server being
 * down.
 */
function resolveUrl(explicit?: string): string | null {
  if (explicit !== undefined) return explicit.trim() || null;

  // A runtime override, so a dev Worker can be pointed at from the console
  // without a rebuild: `window.BACKROOMS_NET_URL = 'ws://localhost:8787/net'`.
  const override = (globalThis as { BACKROOMS_NET_URL?: unknown }).BACKROOMS_NET_URL;
  if (typeof override === 'string' && override.trim()) return override.trim();

  const configured = (import.meta.env?.VITE_NET_URL as string | undefined)?.trim();
  if (configured) return configured;

  if (typeof location === 'undefined') return null;
  if (location.protocol !== 'http:' && location.protocol !== 'https:') return null;
  const scheme = location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${scheme}//${location.host}${DEFAULT_PATH}`;
}

/** Key for the taken set. Item ids are small; a floor never has a million supplies. */
function takenKey(floor: number, item: number): number {
  return floor * 1_000_000 + item;
}

class Client implements NetClient {
  status: NetStatus = 'idle';
  self: PeerId | null = null;
  worldSeed: number | null = null;
  readonly peers: RemotePeer[] = [];

  private readonly url: string | null;
  private name: string;
  private ws: WebSocket | null = null;

  /** Peer records by id, so a `peers` message can update in place. */
  private readonly byId = new Map<PeerId, RemotePeer>();
  /** Names outlive floors: learned from `joined`, kept until the socket dies. */
  private readonly names = new Map<PeerId, string>();
  private readonly takenSet = new Set<number>();
  /** Reused by `handlePeers` so presence costs no allocation at `STATE_HZ`. */
  private readonly seen = new Set<PeerId>();

  /** The floor we last told the server about. Also filters incoming presence. */
  private floor = 0;
  private lastSentAt = 0;
  /** Last published state, for the "nothing has changed" keepalive collapse. */
  private readonly lastSent = { x: NaN, z: NaN, yaw: NaN, eye: NaN, light: false, health: -1 };

  private retryTimer = 0;
  private attempts = 0;
  /** True once a server has ever welcomed us; controls how hard we retry. */
  private everConnected = false;
  /** Set by `denied` and by `dispose`: stop trying, permanently. */
  private refused = false;
  private disposed = false;
  /** True between `disconnect`/`dispose` and the socket actually closing. */
  private closing = false;

  private listeners: Array<Partial<NetHandlers>> = [];

  constructor(opts: NetClientOptions) {
    this.url = resolveUrl(opts.url);
    this.name = (opts.name ?? '').trim().slice(0, 24);
  }

  get online(): boolean {
    return this.status === 'online';
  }

  /* ---------------------------------------------------------------- *
   * Connection
   * ---------------------------------------------------------------- */

  connect(name?: string): void {
    if (name !== undefined) this.name = name.trim().slice(0, 24);
    if (this.disposed || this.url === null) return;
    // A refusal is a real answer (wrong protocol version, world full). Asking
    // again is what the player pressing START means, so it clears it.
    this.refused = false;
    this.closing = false;
    if (this.ws || this.retryTimer) return;
    if (this.status === 'offline') this.attempts = 0;
    this.open();
  }

  private open(): void {
    if (this.disposed || this.url === null || this.ws) return;
    this.setStatus('connecting');
    let ws: WebSocket;
    try {
      ws = new WebSocket(this.url);
    } catch {
      // A malformed URL throws synchronously. Treated as any other failure:
      // the backoff will decide whether it is worth trying again.
      this.scheduleRetry();
      return;
    }
    this.ws = ws;
    ws.onopen = () => {
      if (ws !== this.ws) return;
      this.send({ t: 'hello', version: PROTOCOL_VERSION, name: this.name });
    };
    ws.onmessage = (e) => {
      if (ws === this.ws) this.receive(e.data);
    };
    ws.onerror = () => {
      /* `onclose` always follows; there is nothing useful to say here. */
    };
    ws.onclose = () => {
      if (ws !== this.ws) return;
      this.ws = null;
      this.teardownPeers();
      this.self = null;
      if (this.closing || this.disposed || this.refused) {
        this.setStatus(this.disposed || this.closing ? 'idle' : 'offline');
        return;
      }
      this.scheduleRetry();
    };
  }

  private scheduleRetry(): void {
    this.setStatus('offline');
    if (this.disposed || this.refused || this.url === null) return;

    // Nothing has ever answered here. Most likely nothing ever will — this is
    // a static deploy with no Worker behind it — so stop rather than knock
    // forever. `connect()` is how the next run asks again.
    if (!this.everConnected && this.attempts >= COLD_ATTEMPTS) return;

    const step = Math.min(RETRY_BASE_MS * 2 ** this.attempts, RETRY_MAX_MS);
    this.attempts++;
    // Jitter, so a Worker coming back up does not meet the whole family at once.
    const delay = step * (0.8 + Math.random() * 0.5);
    this.retryTimer = setTimeout(() => {
      this.retryTimer = 0;
      this.open();
    }, delay) as unknown as number;
  }

  disconnect(): void {
    this.closing = true;
    if (this.retryTimer) {
      clearTimeout(this.retryTimer);
      this.retryTimer = 0;
    }
    const ws = this.ws;
    this.ws = null;
    if (ws) {
      ws.onopen = ws.onmessage = ws.onerror = ws.onclose = null;
      try {
        ws.close(1000, 'bye');
      } catch {
        /* already dead */
      }
    }
    this.teardownPeers();
    this.self = null;
    this.setStatus('idle');
  }

  /** Everyone goes away when the socket does; voice needs to hear about it. */
  private teardownPeers(): void {
    if (this.byId.size === 0) return;
    const ids = [...this.byId.keys()];
    this.byId.clear();
    this.peers.length = 0;
    this.names.clear();
    for (const id of ids) this.emit('left', id);
  }

  private setStatus(next: NetStatus): void {
    if (this.status === next) return;
    this.status = next;
    this.emit('status', next);
  }

  /* ---------------------------------------------------------------- *
   * Sending
   * ---------------------------------------------------------------- */

  private send(msg: ClientMessage): void {
    const ws = this.ws;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    try {
      ws.send(JSON.stringify(msg));
    } catch {
      /* A send that fails takes the socket with it; `onclose` will follow. */
    }
  }

  publish(s: Omit<PeerState, 'id'>): void {
    // Before the online check: the floor has to be tracked even while the
    // socket is down, or a client that connects six floors into a run tells the
    // server it is standing in the Lobby.
    if (s.floor !== this.floor) this.setFloor(s.floor);
    if (!this.online) return;

    const now = performance.now();
    const since = now - this.lastSentAt;
    const last = this.lastSent;
    // Health and the torch are *events*, not positions: somebody dying, or a
    // light going out across a room, is the whole point of the packet and
    // holding it for up to a frame of throttle is what makes a friend stand
    // upright for a moment after they are killed. Everything else waits its turn.
    // A whole point of it, because health regenerates continuously: comparing
    // exactly would make every frame of a slow heal an urgent packet.
    const healthMoved = Math.abs(s.health - last.health) >= HEALTH_EPSILON;
    const urgent = healthMoved || s.light !== last.light;
    if (since < SEND_INTERVAL_MS && !urgent) return;

    const still =
      !urgent &&
      Math.abs(s.x - last.x) < MOVE_EPSILON &&
      Math.abs(s.z - last.z) < MOVE_EPSILON &&
      Math.abs(s.yaw - last.yaw) < TURN_EPSILON &&
      Math.abs(s.eye - last.eye) < MOVE_EPSILON;
    // Standing still costs one message a second instead of fifteen. The server
    // drops silent players after `PEER_TIMEOUT`, so the keepalive is not
    // optional — it is just much cheaper than the truth repeated.
    if (still && since < KEEPALIVE_MS) return;

    this.lastSentAt = now;
    last.x = s.x;
    last.z = s.z;
    last.yaw = s.yaw;
    last.eye = s.eye;
    last.light = s.light;
    last.health = s.health;
    this.send({ t: 'state', s });
  }

  setFloor(floor: number): void {
    if (floor === this.floor) return;
    this.floor = floor;
    // Everyone we could see is on the floor we just left.
    this.dropAllPeers();
    this.send({ t: 'floor', floor });
  }

  take(floor: number, item: number): void {
    this.send({ t: 'take', floor, item });
  }

  strike(target: PeerId, amount: number): void {
    if (!Number.isFinite(amount) || amount <= 0) return;
    this.send({ t: 'strike', target, amount });
  }

  say(text: string): void {
    const line = text.trim().slice(0, SAY_MAX);
    if (!line) return;
    this.send({ t: 'say', text: line });
  }

  signal(to: PeerId, data: unknown): void {
    this.send({ t: 'signal', to, data });
  }

  /* ---------------------------------------------------------------- *
   * Receiving
   * ---------------------------------------------------------------- */

  private receive(raw: unknown): void {
    if (typeof raw !== 'string') return;
    let msg: ServerMessage;
    try {
      const parsed: unknown = JSON.parse(raw);
      if (typeof parsed !== 'object' || parsed === null) return;
      if (typeof (parsed as { t?: unknown }).t !== 'string') return;
      msg = parsed as ServerMessage;
    } catch {
      return;
    }

    try {
      this.dispatch(msg);
    } catch {
      // A malformed message must not be able to end the session. Dropping it
      // costs at most one frame of somebody's position.
    }
  }

  private dispatch(msg: ServerMessage): void {
    switch (msg.t) {
      case 'welcome':
        this.handleWelcome(msg);
        return;
      case 'peers':
        this.handlePeers(msg.peers);
        return;
      case 'joined': {
        if (typeof msg.name === 'string' && msg.name) this.names.set(msg.id, msg.name);
        const known = this.byId.get(msg.id);
        if (known) known.name = this.names.get(msg.id) ?? '';
        this.emit('joined', { id: msg.id, name: msg.name ?? '', floor: msg.floor });
        return;
      }
      case 'left':
        this.names.delete(msg.id);
        if (this.byId.delete(msg.id)) this.rebuildPeerList();
        this.emit('left', msg.id);
        return;
      case 'taken':
        this.takenSet.add(takenKey(msg.floor, msg.item));
        this.emit('taken', { floor: msg.floor, item: msg.item, by: msg.by, mine: msg.by === this.self });
        return;
      case 'struck':
        this.emit('struck', { by: msg.by, amount: msg.amount });
        return;
      case 'said':
        this.emit('said', { from: msg.from, text: String(msg.text ?? '').slice(0, SAY_MAX) });
        return;
      case 'signal':
        this.emit('signal', { from: msg.from, data: msg.data });
        return;
      case 'denied':
        // A refusal is an answer. Retrying a version mismatch a hundred times
        // would be exactly the spam this module is written to avoid.
        this.refused = true;
        this.closing = true;
        this.disconnect();
        this.setStatus('offline');
        return;
      default:
        // A newer server saying something this build does not know about is
        // not an error; it is a message for a later version of this file.
        return;
    }
  }

  private handleWelcome(msg: Extract<ServerMessage, { t: 'welcome' }>): void {
    if (msg.version !== PROTOCOL_VERSION) {
      this.refused = true;
      this.closing = true;
      this.disconnect();
      this.setStatus('offline');
      return;
    }
    this.everConnected = true;
    this.attempts = 0;
    this.self = msg.you;
    this.worldSeed = msg.worldSeed;
    this.takenSet.clear();
    for (const t of msg.taken ?? []) this.takenSet.add(takenKey(t.floor, t.item));

    // Tell the server where we are: a reconnect lands on a fresh connection
    // that assumes floor 0, and this client may well be six floors down.
    this.send({ t: 'floor', floor: this.floor });
    // Force the next `publish` through the "has anything changed" gate.
    this.lastSent.health = -1;
    this.lastSentAt = 0;

    this.setStatus('online');
    // `welcome.peers` carries no names — see the note on `RemotePeer.name`.
    // Whoever is already standing here is nameless until the server sends a
    // `joined` for them, which is a server-side fix needing no protocol change.
    this.handlePeers(msg.peers ?? []);
    this.emit('welcome', { you: msg.you, worldSeed: msg.worldSeed, taken: msg.taken ?? [] });
  }

  /**
   * Replace the presence set. The server already filters to our floor; the
   * second check is for the packet that was in flight when we descended, which
   * would otherwise park somebody from the floor above in our corridor.
   */
  private handlePeers(list: readonly PeerState[]): void {
    if (!Array.isArray(list)) return;
    const now = performance.now();
    const seen = this.seen;
    seen.clear();
    let membershipChanged = false;

    for (const s of list) {
      if (!s || typeof s.id !== 'string') continue;
      if (s.id === this.self) continue;
      if (s.floor !== this.floor) continue;
      seen.add(s.id);
      const peer = this.byId.get(s.id);
      if (peer === undefined) {
        const fresh: RemotePeer = { ...s, name: this.names.get(s.id) ?? '', at: now };
        this.byId.set(s.id, fresh);
        membershipChanged = true;
      } else {
        peer.floor = s.floor;
        peer.x = s.x;
        peer.z = s.z;
        peer.yaw = s.yaw;
        peer.eye = s.eye;
        peer.light = !!s.light;
        peer.health = s.health;
        peer.at = now;
      }
    }

    // Anyone not in this snapshot has walked off the floor or gone quiet.
    // Membership rather than a timestamp: `performance.now()` is deliberately
    // coarse in browsers and two snapshots can share a value.
    for (const id of this.byId.keys()) {
      if (seen.has(id)) continue;
      this.byId.delete(id);
      membershipChanged = true;
    }
    if (membershipChanged) this.rebuildPeerList();
  }

  /** Drop presence without claiming anybody disconnected — they changed floor. */
  private dropAllPeers(): void {
    if (this.byId.size === 0) return;
    this.byId.clear();
    this.peers.length = 0;
  }

  private rebuildPeerList(): void {
    this.peers.length = 0;
    for (const peer of this.byId.values()) this.peers.push(peer);
  }

  /* ---------------------------------------------------------------- *
   * Subscription
   * ---------------------------------------------------------------- */

  on(handlers: Partial<NetHandlers>): () => void {
    this.listeners.push(handlers);
    return () => {
      const i = this.listeners.indexOf(handlers);
      if (i >= 0) this.listeners.splice(i, 1);
    };
  }

  private emit<K extends keyof NetHandlers>(type: K, arg: Parameters<NetHandlers[K]>[0]): void {
    // Snapshot: a handler is allowed to unsubscribe itself from inside itself.
    for (const listener of [...this.listeners]) {
      const fn = listener[type] as ((a: unknown) => void) | undefined;
      if (!fn) continue;
      try {
        fn(arg);
      } catch {
        // Somebody else's bug is not this socket's problem, and it is certainly
        // not the frame loop's.
      }
    }
  }

  isTaken(floor: number, item: number): boolean {
    return this.takenSet.has(takenKey(floor, item));
  }

  nameOf(id: PeerId): string {
    return this.names.get(id) ?? '';
  }

  dispose(): void {
    this.disposed = true;
    this.disconnect();
    this.listeners = [];
    this.takenSet.clear();
  }
}

/**
 * Build the client. Constructing one is free and connects to nothing: call
 * `connect()` when a run starts, which is both the lazy moment and the one the
 * player would understand as "join".
 */
export function makeNetClient(opts: NetClientOptions = {}): NetClient {
  return new Client(opts);
}
